/**
 * Auto Strategy — IdeaWorks
 * Einstein-powered automated content strategy:
 * - Trending topic discovery (AI-researched daily)
 * - Weekly content schedule with Einstein recommendations
 * - One-click content generation + publish queue
 * - Engagement feedback loop
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Zap, RefreshCw, Brain, Calendar, BarChart2,
  CheckCircle, Clock, ChevronDown, ChevronUp, Copy, Check,
  Flame, Target, Star, ThumbsUp, ThumbsDown, Send,
  Lightbulb, Globe, Play, Sparkles, FlaskConical, Plus, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = ['LinkedIn', 'Twitter/X', 'Instagram', 'TikTok', 'Reels (Meta)', 'YouTube', 'Facebook'];
const PLATFORM_COLORS = {
  'LinkedIn': '#0A66C2', 'Twitter/X': '#1DA1F2', 'Instagram': '#E1306C',
  'TikTok': '#69C9D0', 'Reels (Meta)': '#8B5CF6', 'YouTube': '#FF0000', 'Facebook': '#1877F2',
};

const URGENCY_COLORS = { high: '#f97316', medium: '#fbbf24', low: '#3dffa0' };

function TopicCard({ topic, onGenerate, onSelect, selected }) {
  return (
    <div
      onClick={() => onSelect(topic)}
      className="rounded-2xl p-4 cursor-pointer transition-all hover:scale-[1.01] group"
      style={{
        background: selected ? 'rgba(124,94,250,0.12)' : 'var(--llmc-surface)',
        border: `1px solid ${selected ? 'rgba(124,94,250,0.5)' : 'var(--llmc-border)'}`,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 text-sm font-bold text-white leading-snug">{topic.topic}</div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: `${URGENCY_COLORS[topic.urgency] || '#fbbf24'}20`, color: URGENCY_COLORS[topic.urgency] || '#fbbf24' }}>
            {topic.urgency}
          </span>
          <span className="text-[11px] font-black w-8 text-right" style={{ color: '#a68eff' }}>{topic.score}</span>
        </div>
      </div>
      <div className="text-[11px] mb-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
        {topic.angle || topic.why_trending}
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {(topic.platforms || []).slice(0, 3).map(p => (
          <span key={p} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
            style={{ background: `${PLATFORM_COLORS[p] || '#666'}20`, color: PLATFORM_COLORS[p] || '#888' }}>
            {p}
          </span>
        ))}
      </div>
      <button
        onClick={e => { e.stopPropagation(); onGenerate(topic); }}
        data-testid={`generate-topic-btn-${topic.id}`}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all opacity-0 group-hover:opacity-100"
        style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
        <Zap size={11} /> Generate Content
      </button>
    </div>
  );
}

function ScheduleDay({ day, isWeekend }) {
  const [open, setOpen] = useState(!isWeekend);
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--llmc-border)' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left"
        style={{ background: 'var(--llmc-surface)' }}>
        <div className="flex items-center gap-3">
          <div className="text-sm font-bold text-white">{day.day}</div>
          <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{day.date}</div>
          {isWeekend && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>
              Weekend · max 1
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0' }}>
            {(day.posts || []).length} post{(day.posts || []).length !== 1 ? 's' : ''}
          </span>
        </div>
        {open ? <ChevronUp size={14} style={{ color: 'var(--llmc-text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--llmc-text-muted)' }} />}
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-3" style={{ background: 'rgba(255,255,255,0.02)' }}>
          {(day.posts || []).map((post, i) => (
            <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-1.5">
                <Clock size={11} style={{ color: '#a68eff' }} />
                <span className="text-[10px] font-bold" style={{ color: '#a68eff' }}>{post.time}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={{ background: `${PLATFORM_COLORS[post.platform] || '#666'}20`, color: PLATFORM_COLORS[post.platform] || '#888' }}>
                  {post.platform}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
                  {post.content_type}
                </span>
              </div>
              <div className="text-xs font-semibold text-white mb-1">{post.topic}</div>
              <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{post.why_this_works}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AutoStrategy() {
  const [topics, setTopics] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [insights, setInsights] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('LinkedIn');
  const [selectedContentType, setSelectedContentType] = useState('blog');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loading, setLoading] = useState({ topics: false, schedule: false, generate: false, research: false, campaign: false });
  const [activeTab, setActiveTab] = useState('topics');
  const [copiedId, setCopiedId] = useState(null);
  const [feedbackSent, setFeedbackSent] = useState({});
  const [niche, setNiche] = useState('AI & Cybersecurity');
  const [customNiche, setCustomNiche] = useState('');
  const [showCustomNiche, setShowCustomNiche] = useState(false);
  const [brandId] = useState('default');
  const [einsteinNote, setEinsteinNote] = useState('');
  const [profileNiches, setProfileNiches] = useState([]);
  const navigate = useNavigate();

  const DEFAULT_NICHES = ['AI & Cybersecurity', 'Threat Intelligence', 'Generative AI', 'AI Marketing', 'Machine Learning', 'SaaS & Product', 'Tech Startups', 'Cloud Computing', 'Future of Work', 'B2B Sales', 'Leadership & Management', 'Digital Transformation', 'Data Science', 'Blockchain & Web3', 'Product Management'];

  const load = useCallback(async () => {
    setLoading(l => ({ ...l, topics: true }));
    try {
      const [topicsRes, scheduleRes, insightsRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/trends/topics?brand_id=${brandId}`),
        axios.get(`${API}/api/trends/schedule?brand_id=${brandId}`),
        axios.get(`${API}/api/trends/insights?brand_id=${brandId}`),
        axios.get(`${API}/api/onboarding/profile`).catch(() => ({ data: {} })),
      ]);
      setTopics(topicsRes.data.topics || []);
      setSchedule(scheduleRes.data.strategy || null);
      if (scheduleRes.data.strategy?.einstein_note) setEinsteinNote(scheduleRes.data.strategy.einstein_note);
      setInsights(insightsRes.data);
      // Load focus_topics from onboarding profile
      const focusTopics = profileRes.data?.profile?.focus_topics || [];
      if (focusTopics.length > 0) {
        setProfileNiches(focusTopics);
        // Set the niche to the first profile topic if it's still the default
        setNiche(n => n === 'AI & Cybersecurity' ? focusTopics[0] : n);
      }
    } catch {}
    setLoading(l => ({ ...l, topics: false }));
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const researchTopics = async () => {
    setLoading(l => ({ ...l, research: true }));
    try {
      const { data } = await axios.post(`${API}/api/trends/research`, {
        niche, brand_id: brandId, niches: [niche],
      });
      setTopics(data.topics || []);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Research failed');
    }
    setLoading(l => ({ ...l, research: false }));
  };

  const buildSchedule = async () => {
    setLoading(l => ({ ...l, schedule: true }));
    try {
      const { data } = await axios.post(`${API}/api/trends/auto-strategy`, {
        brand_id: brandId, platforms: PLATFORMS, days: 7,
      });
      setSchedule(data.strategy || null);
      if (data.strategy?.einstein_note) setEinsteinNote(data.strategy.einstein_note);
      await load();
      setActiveTab('schedule');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Strategy failed');
    }
    setLoading(l => ({ ...l, schedule: false }));
  };

  const runOneCampaign = async () => {
    setLoading(l => ({ ...l, campaign: true }));
    try {
      // 1. Research topics first
      const researchRes = await axios.post(`${API}/api/trends/research`, {
        niche, brand_id: brandId, niches: [niche],
      });
      const topTopics = (researchRes.data.topics || []).slice(0, 3);

      // 2. Repurpose top topic across all platforms
      if (topTopics.length > 0) {
        const topTopic = topTopics[0];
        const repurposeRes = await axios.post(`${API}/api/repurpose/transform`, {
          content: `${topTopic.topic}\n\n${topTopic.angle}\n\n${topTopic.why_trending}`,
          title: topTopic.topic,
          content_type: 'blog',
          platforms: ['linkedin', 'twitter_x', 'instagram', 'tiktok'],
          brand_id: brandId,
        });
        alert(`Campaign launched! Generated content for ${repurposeRes.data.success_count} platforms. Check Content Repurposer for results.`);
        setTopics(researchRes.data.topics || []);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Campaign failed');
    }
    setLoading(l => ({ ...l, campaign: false }));
  };

  const generateForTopic = async (topic) => {
    setSelectedTopic(topic);
    setLoading(l => ({ ...l, generate: true }));
    setGeneratedContent(null);
    try {
      const { data } = await axios.post(`${API}/api/trends/auto-generate`, {
        topic: topic.topic,
        brand_id: brandId,
        platform: selectedPlatform,
        content_type: selectedContentType,
        use_trending: false,
      });
      setGeneratedContent(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Generation failed');
    }
    setLoading(l => ({ ...l, generate: false }));
  };

  const sendFeedback = async (contentId, signal) => {
    try {
      await axios.post(`${API}/api/trends/feedback`, {
        content_id: contentId, signal, platform: selectedPlatform,
      });
      setFeedbackSent(f => ({ ...f, [contentId]: signal }));
    } catch {}
  };

  const copyContent = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const TABS = [
    { id: 'topics', label: 'Trending Topics', icon: Flame },
    { id: 'schedule', label: 'Content Schedule', icon: Calendar },
    { id: 'insights', label: 'Performance Insights', icon: BarChart2 },
  ];

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(124,94,250,0.3), rgba(61,255,160,0.15))', border: '1px solid rgba(124,94,250,0.4)' }}>
                  <Brain size={18} style={{ color: '#a68eff' }} />
                </div>
                <div>
                  <h1 className="text-lg font-black text-white">Auto Strategy Engine</h1>
                  <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>AI researches trends · Einstein plans · You approve</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={researchTopics} disabled={loading.research} data-testid="research-trends-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }}>
                {loading.research ? <RefreshCw size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                Research Now
              </button>
              <button onClick={buildSchedule} disabled={loading.schedule} data-testid="build-schedule-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
                {loading.schedule ? <RefreshCw size={12} className="animate-spin" /> : <Brain size={12} />}
                Einstein: Build Schedule
              </button>
              <button onClick={runOneCampaign} disabled={loading.campaign} data-testid="one-click-campaign-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a12' }}>
                {loading.campaign ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                1-Click Campaign
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  background: activeTab === t.id ? 'rgba(124,94,250,0.15)' : 'transparent',
                  border: `1px solid ${activeTab === t.id ? 'rgba(124,94,250,0.4)' : 'transparent'}`,
                  color: activeTab === t.id ? '#a68eff' : 'var(--llmc-text-muted)',
                }}>
                <t.icon size={11} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Topics tab */}
          {activeTab === 'topics' && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 max-w-sm">
                  {showCustomNiche ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        value={customNiche}
                        onChange={e => setCustomNiche(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && customNiche.trim()) { setNiche(customNiche.trim()); setShowCustomNiche(false); } }}
                        placeholder="Enter custom topic..."
                        data-testid="custom-niche-input"
                        autoFocus
                        className="llmc-input px-3 py-1.5 text-xs flex-1"
                      />
                      <button onClick={() => { if (customNiche.trim()) { setNiche(customNiche.trim()); } setShowCustomNiche(false); }}
                        className="p-1.5 rounded-lg" style={{ background: 'rgba(61,255,160,0.15)', color: '#3dffa0' }}>
                        <Check size={12} />
                      </button>
                      <button onClick={() => setShowCustomNiche(false)} className="p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <select value={niche} onChange={e => setNiche(e.target.value)}
                        className="llmc-input px-3 py-1.5 text-xs flex-1"
                        data-testid="niche-select">
                        {/* Profile topics first (if any) */}
                        {profileNiches.length > 0 && (
                          <optgroup label="Your Topics">
                            {profileNiches.map(n => (
                              <option key={`profile-${n}`} value={n}>{n}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="Popular Niches">
                          {[...DEFAULT_NICHES, ...(customNiche && !DEFAULT_NICHES.includes(customNiche) && !profileNiches.includes(customNiche) ? [customNiche] : [])].map(n => (
                            <option key={n}>{n}</option>
                          ))}
                        </optgroup>
                      </select>
                      <button onClick={() => setShowCustomNiche(true)}
                        data-testid="add-custom-niche-btn"
                        title="Add custom topic"
                        className="p-1.5 rounded-lg flex-shrink-0 hover:opacity-80 transition-all"
                        style={{ background: 'rgba(124,94,250,0.15)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <span className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>{topics.length} topics discovered</span>
              </div>

              {loading.research ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center space-y-3">
                    <RefreshCw size={32} className="animate-spin mx-auto" style={{ color: '#f97316' }} />
                    <div className="text-sm text-white font-semibold">AI is researching trending topics...</div>
                    <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Scanning industry signals, discussions, and emerging stories</div>
                  </div>
                </div>
              ) : topics.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)' }}>
                    <TrendingUp size={32} style={{ color: '#f97316' }} />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-white mb-1">No topics yet</div>
                    <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Click "Research Now" to discover trending topics</div>
                  </div>
                  <button onClick={researchTopics}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                    style={{ background: 'rgba(249,115,22,0.2)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)' }}>
                    <TrendingUp size={14} /> Research Trending Topics
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  {topics.map(topic => (
                    <TopicCard
                      key={topic.id}
                      topic={topic}
                      onGenerate={generateForTopic}
                      onSelect={setSelectedTopic}
                      selected={selectedTopic?.id === topic.id}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Schedule tab */}
          {activeTab === 'schedule' && (
            <>
              {einsteinNote && (
                <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)' }}>
                  <Brain size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#a68eff' }} />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#a68eff' }}>Einstein's Strategy Note</div>
                    <div className="text-xs leading-relaxed text-white">{einsteinNote}</div>
                  </div>
                </div>
              )}

              {schedule?.key_themes?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {schedule.key_themes.map((t, i) => (
                    <span key={i} className="text-[11px] px-3 py-1 rounded-full"
                      style={{ background: 'rgba(61,255,160,0.08)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.2)' }}>
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {!schedule ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(124,94,250,0.1)' }}>
                    <Calendar size={32} style={{ color: '#a68eff' }} />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-white mb-1">No schedule yet</div>
                    <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Click "Einstein: Build Schedule" to generate a weekly content plan</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(schedule.schedule || []).map((day, i) => {
                    const dt = new Date(day.date + 'T12:00:00');
                    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                    return <ScheduleDay key={i} day={day} isWeekend={isWeekend} />;
                  })}
                </div>
              )}
            </>
          )}

          {/* Insights tab */}
          {activeTab === 'insights' && (
            <div className="space-y-4">
              {!insights || insights.total_signals === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-3">
                  <BarChart2 size={40} style={{ color: 'rgba(255,255,255,0.15)' }} />
                  <div className="text-sm font-bold text-white">No engagement data yet</div>
                  <div className="text-xs text-center max-w-sm" style={{ color: 'var(--llmc-text-muted)' }}>
                    Generate content and provide feedback (like/dislike) to build engagement insights. Einstein uses this to improve future recommendations.
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="rounded-2xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-2xl font-black" style={{ color: '#3dffa0' }}>{insights.total_signals}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--llmc-text-muted)' }}>Total Signals</div>
                    </div>
                    <div className="rounded-2xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-2xl font-black" style={{ color: '#a68eff' }}>{insights.content_generated}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--llmc-text-muted)' }}>Generated</div>
                    </div>
                    <div className="rounded-2xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-sm font-black text-white">{insights.best_platform}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--llmc-text-muted)' }}>Best Platform</div>
                    </div>
                  </div>

                  {insights.top_topics?.length > 0 && (
                    <div className="rounded-2xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-sm font-bold text-white mb-3">Top Performing Topics</div>
                      <div className="space-y-2">
                        {insights.top_topics.map((t, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <Star size={11} style={{ color: '#fbbf24' }} />
                            <div className="flex-1 text-xs text-white truncate">{t.topic}</div>
                            <div className="text-xs font-bold" style={{ color: '#3dffa0' }}>{t.score} pts</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right panel: Generation */}
      <div className="w-96 flex-shrink-0 flex flex-col overflow-hidden" style={{ borderLeft: '1px solid var(--llmc-border)' }}>
        <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Zap size={14} style={{ color: '#3dffa0' }} />
            <span className="text-sm font-bold text-white">Generate Content</span>
          </div>

          {selectedTopic ? (
            <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.3)' }}>
              <div className="text-[10px] font-bold uppercase mb-1" style={{ color: '#a68eff' }}>Selected Topic</div>
              <div className="text-xs font-semibold text-white leading-snug">{selectedTopic.topic}</div>
            </div>
          ) : (
            <div className="rounded-xl p-2 mb-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)' }}>
              <Target size={11} style={{ color: 'rgba(255,255,255,0.3)' }} />
              <span className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>Click a topic card to select it</span>
            </div>
          )}

          <div className="space-y-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Platform</label>
              <div className="grid grid-cols-2 gap-1">
                {PLATFORMS.map(p => (
                  <button key={p} onClick={() => setSelectedPlatform(p)}
                    className="px-2 py-1.5 rounded-lg text-[10px] font-semibold text-left transition-all truncate"
                    style={{
                      background: selectedPlatform === p ? `${PLATFORM_COLORS[p] || '#7c5efa'}20` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${selectedPlatform === p ? PLATFORM_COLORS[p] || '#7c5efa' : 'rgba(255,255,255,0.08)'}`,
                      color: selectedPlatform === p ? PLATFORM_COLORS[p] || '#a68eff' : 'rgba(255,255,255,0.45)',
                    }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Content Type</label>
              <select value={selectedContentType} onChange={e => setSelectedContentType(e.target.value)} className="llmc-input w-full px-3 py-1.5 text-xs">
                {['blog', 'social', 'video_script', 'newsletter'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <button
            onClick={() => selectedTopic && generateForTopic(selectedTopic)}
            disabled={!selectedTopic || loading.generate}
            data-testid="auto-generate-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
            {loading.generate ? <><RefreshCw size={13} className="animate-spin" /> Generating...</> : <><Zap size={13} /> Generate</>}
          </button>
        </div>

        {/* Generated content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading.generate && (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-pulse" style={{ background: 'rgba(124,94,250,0.2)' }}>
                <Brain size={20} style={{ color: '#a68eff' }} />
              </div>
              <div className="text-xs text-center" style={{ color: 'var(--llmc-text-muted)' }}>Einstein is crafting {selectedPlatform} content...</div>
            </div>
          )}

          {generatedContent?.content && !loading.generate && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#a68eff' }}>{generatedContent.platform} Content</div>
                <div className="text-xs font-bold text-white mb-2">{generatedContent.content.title}</div>
              </div>

              {generatedContent.content.hook && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
                  <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#f97316' }}>Hook</div>
                  <div className="text-xs text-white">{generatedContent.content.hook}</div>
                </div>
              )}

              <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="text-[9px] font-bold uppercase mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Content</div>
                <div className="text-xs leading-relaxed whitespace-pre-wrap text-white" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {generatedContent.content.content || generatedContent.content.tweet || generatedContent.content.post || generatedContent.content.script || generatedContent.content.caption}
                </div>
              </div>

              {generatedContent.content.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {generatedContent.content.hashtags.map((h, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(124,94,250,0.1)', color: '#a68eff' }}>{h}</span>
                  ))}
                </div>
              )}

              {generatedContent.content.best_time && (
                <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>
                  <Clock size={11} /> Best time: <span className="text-white">{generatedContent.content.best_time}</span>
                </div>
              )}

              {generatedContent.content.why_this_works && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}>
                  <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#3dffa0' }}>Why This Works</div>
                  <div className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{generatedContent.content.why_this_works}</div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => sendFeedback(generatedContent.content_id, 'like')}
                    disabled={!!feedbackSent[generatedContent.content_id]}
                    className="p-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-40"
                    style={{ background: feedbackSent[generatedContent.content_id] === 'like' ? 'rgba(61,255,160,0.2)' : 'rgba(255,255,255,0.05)', color: '#3dffa0' }}>
                    <ThumbsUp size={13} />
                  </button>
                  <button
                    onClick={() => sendFeedback(generatedContent.content_id, 'dislike')}
                    disabled={!!feedbackSent[generatedContent.content_id]}
                    className="p-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-40"
                    style={{ background: feedbackSent[generatedContent.content_id] === 'dislike' ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.05)', color: '#f97316' }}>
                    <ThumbsDown size={13} />
                  </button>
                </div>
                <button
                  onClick={() => {
                    const c = generatedContent.content;
                    const text = [c.title, c.content || c.tweet || c.post || c.script || c.caption, (c.hashtags || []).join(' ')].filter(Boolean).join('\n\n');
                    copyContent(text, generatedContent.content_id);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
                  {copiedId === generatedContent.content_id ? <Check size={11} style={{ color: '#3dffa0' }} /> : <Copy size={11} />}
                  {copiedId === generatedContent.content_id ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* ── Deep-Dive Launch Buttons ── */}
              <div className="pt-3 space-y-2" style={{ borderTop: '1px solid var(--llmc-border)' }}>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Take This Further</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      const topicText = generatedContent.content.title || generatedContent.topic || '';
                      const contentText = generatedContent.content.content || generatedContent.content.post || '';
                      const intelligentPrompt = `I've generated a ${generatedContent.platform} ${selectedContentType} on "${topicText}". Here's the draft:\n\n${contentText}\n\nIntent: ${generatedContent.einsteins_note || 'To establish thought leadership on this topic'}. Target audience: ${generatedContent.content.target_audience || 'professionals'}. Please help me brainstorm deeper angles, counterpoints, and stronger narratives.`;
                      localStorage.setItem('ideaworks_prefill_idea', topicText);
                      localStorage.setItem('ideaworks_prefill_context', JSON.stringify({ topic: topicText, content: contentText, intelligentPrompt, source: 'auto-strategy', platform: generatedContent.platform }));
                      window.location.href = '/brainstorm';
                    }}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105"
                    style={{ background: 'rgba(166,142,255,0.12)', border: '1px solid rgba(166,142,255,0.3)', color: '#a68eff' }}>
                    <Brain size={12} /> Brainstorm with Council
                  </button>
                  <button
                    onClick={() => {
                      const topicText = generatedContent.content.title || generatedContent.topic || '';
                      const contentText = generatedContent.content.content || generatedContent.content.post || '';
                      const intelligentPrompt = `Research Lab: I need to deepen this article on "${topicText}". Audience: ${generatedContent.content.target_audience || 'professionals'}. Platform: ${generatedContent.platform}. Current draft:\n\n${contentText}\n\nFind supporting data, recent studies, expert quotes, and statistics that strengthen the key argument.`;
                      localStorage.setItem('ideaworks_prefill_idea', topicText);
                      localStorage.setItem('ideaworks_prefill_context', JSON.stringify({ topic: topicText, content: contentText, intelligentPrompt, source: 'auto-strategy', platform: generatedContent.platform }));
                      window.location.href = '/research';
                    }}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105"
                    style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.25)', color: '#3dffa0' }}>
                    <FlaskConical size={12} /> Deep Research
                  </button>
                </div>
                {/* Content Studio button */}
                <button
                  onClick={() => {
                    const t = generatedContent.content.title || generatedContent.topic || '';
                    const c = generatedContent.content.content || generatedContent.content.post || '';
                    localStorage.setItem('studio_topic', t);
                    if (c) localStorage.setItem('studio_content', c);
                    navigate('/content-studio');
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105"
                  style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)', color: '#f97316' }}>
                  <Globe size={12} /> Open in Content Studio (6 channels)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
